import { useDndMonitor } from '@dnd-kit/core';
import { cn } from '@tempo-io/sui-class-utils';
import { addDays, format } from 'date-fns';
import { useState } from 'react';
import { ITEM_HEIGHT, ITEM_LANE_HEIGHT, ITEM_LANE_HEIGHT_COMPACT } from './TimelineItem';
import { darkenTimelineBgColor } from './utils/timelineColorUtils';

export const TimelineItemDragOverlayContent = ({
  name,
  color,
  startDate,
  endDate,
  dayWidth,
  isCompact,
}: {
  name: string;
  color: string;
  startDate: string;
  endDate: string;
  dayWidth: number;
  isCompact?: boolean;
}) => {
  const laneHeight = isCompact ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT;
  const itemTopOffset = isCompact ? 1 : laneHeight / 2 - ITEM_HEIGHT / 2 + 2;
  const progressBarColor = darkenTimelineBgColor(color, 10);
  const [prospectiveDates, setProspectiveDates] = useState<{
    start: Date;
    end: Date;
  } | null>(null);

  useDndMonitor({
    onDragMove: (event) => {
      const { delta } = event;
      if (!delta || !startDate || !endDate) return;

      const daysOffset = Math.round(delta.x / dayWidth);

      if (daysOffset !== 0) {
        const originalStart = new Date(startDate);
        const originalEnd = new Date(endDate);

        setProspectiveDates({
          start: addDays(originalStart, daysOffset),
          end: addDays(originalEnd, daysOffset),
        });
      }
    },
    onDragEnd: () => {
      setProspectiveDates(null);
    },
  });

  const displayStartDate = prospectiveDates?.start || (startDate ? new Date(startDate) : null);
  const displayEndDate = prospectiveDates?.end || (endDate ? new Date(endDate) : null);

  return (
    <div
      className={cn('shadow-lg px-2 flex items-center relative', {
        'rounded-lg': !isCompact,
      })}
      style={{
        backgroundColor: color,
        minWidth: '120px',
        height: ITEM_HEIGHT,
        marginTop: itemTopOffset,
      }}
    >
      <span className="text-xs font-semibold truncate text-text-neutral-inverted relative z-10">
        {name}
      </span>
      <div
        className={cn('absolute inset-0', {
          'rounded-lg': !isCompact,
        })}
        style={{
          backgroundColor: progressBarColor,
        }}
      />
      {displayStartDate && displayEndDate && (
        <div className="absolute -bottom-14 left-0 right-0 mx-auto w-max bg-bg-surface-base text-text-neutral-primary border-2 text-xs px-3 py-2 rounded shadow-lg whitespace-nowrap z-50">
          <div className="font-semibold">{name}</div>
          <div>
            {format(displayStartDate, 'MMM d, yyyy')} - {format(displayEndDate, 'MMM d, yyyy')}
          </div>
        </div>
      )}
    </div>
  );
};
